// Portfolio Administration v1.0 PR 6 — horizontal version timeline rendered
// on the waterfall detail page. Each node represents one WaterfallConfig
// version; clicking a node loads its priorities into the editor below.

import { cn } from "@/design-system/lib/utils";
import type { WaterfallConfigDto } from "@/api/portfolioAdmin/pr6Types";
import { WaterfallStatusBadge } from "./WaterfallStatusBadge";

interface VersionTimelineProps {
  versions: WaterfallConfigDto[];
  selectedConfigId: string | null;
  onSelect: (configId: string) => void;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 10);
}

export function VersionTimeline({
  versions,
  selectedConfigId,
  onSelect,
}: VersionTimelineProps) {
  if (versions.length === 0) {
    return (
      <p className="text-small text-gray-mid italic m-0">
        No versions yet.
      </p>
    );
  }
  // Sort ascending: v1 → v2 → v3 ...
  const ordered = [...versions].sort((a, b) => a.version - b.version);
  return (
    <div
      className="flex flex-wrap items-center gap-space-2"
      role="list"
      aria-label="Version timeline"
      data-testid="waterfall-version-timeline"
    >
      {ordered.map((v, i) => (
        <div key={v.id} className="flex items-center gap-space-2" role="listitem">
          <button
            type="button"
            onClick={() => onSelect(v.id)}
            className={cn(
              "flex flex-col items-start gap-[2px] px-space-3 py-space-2 rounded-md",
              "border-[1.5px] transition-colors text-left",
              v.id === selectedConfigId
                ? "border-gold bg-gold-highlight/20"
                : "border-border-light bg-white-pure hover:border-gold-shadow",
            )}
            data-testid={`waterfall-version-node-${v.version}`}
            aria-pressed={v.id === selectedConfigId}
          >
            <span className="font-display font-bold text-small">v{v.version}</span>
            <WaterfallStatusBadge status={v.status} />
            <span className="text-caption text-gray-mid">
              {v.status === "active"
                ? `since ${fmtDate(v.effectiveStartDate)}`
                : v.status === "superseded"
                  ? `until ${fmtDate(v.effectiveEndDate)}`
                  : v.proposedDate
                    ? `proposed ${fmtDate(v.proposedDate)}`
                    : "in flight"}
            </span>
          </button>
          {i < ordered.length - 1 && (
            <span className="text-gray-mid" aria-hidden="true">
              →
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
